/**
 * Offline Sync Manager
 * Handles background synchronization of offline transactions
 */

import {
  getPendingTransactions,
  updateTransaction,
  getTransactionCounts,
  logSyncHistory,
  logConflict,
  recoverStuckTransactions
} from './offlineDb.js';

const MAX_SYNC_ATTEMPTS = 5;
const SYNC_INTERVAL_MS = 30000; // 30 seconds
const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24 hours

// Event emitter for sync status updates
const listeners = new Map();

/**
 * Emit an event to all listeners
 */
function emit(event, data) {
  const eventListeners = listeners.get(event) || [];
  eventListeners.forEach(callback => {
    try {
      callback(data);
    } catch (err) {
      console.error(`[OfflineSync] Error in ${event} listener:`, err);
    }
  });
}

/**
 * Subscribe to sync events
 * @param {string} event - Event name ('sync_start', 'sync_complete', 'sync_error', 'conflict', 'status_change')
 * @param {Function} callback - Event handler
 * @returns {Function} Unsubscribe function
 */
export function subscribe(event, callback) {
  if (!listeners.has(event)) {
    listeners.set(event, []);
  }
  const eventListeners = listeners.get(event);

  // ป้องกัน duplicate listeners
  if (!eventListeners.includes(callback)) {
    eventListeners.push(callback);
  }

  // Return unsubscribe function
  return () => {
    const index = eventListeners.indexOf(callback);
    if (index > -1) {
      eventListeners.splice(index, 1);
    }
  };
}

// Sync state
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
let isSyncing = false;
let syncIntervalId = null;
let apiUrl = '';

/**
 * Initialize the sync manager
 * @param {string} baseApiUrl - The API base URL
 */
export async function initSyncManager(baseApiUrl) {
  apiUrl = baseApiUrl;

  // Setup online/offline listeners
  if (typeof window !== 'undefined') {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Sync when tab becomes visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && isOnline) {
        console.log('[OfflineSync] Tab visible - checking for pending transactions');
        triggerSync();
      }
    });
  }

  // Recover any stuck transactions from previous session
  const recovered = await recoverStuckTransactions();
  if (recovered > 0) {
    emit('status_change', await getStatus());
  }

  // Start periodic sync
  startPeriodicSync();

  // Initial sync if online
  if (isOnline) {
    triggerSync();
  }

  console.log('[OfflineSync] Sync manager initialized');
}

/**
 * Cleanup the sync manager
 */
export function destroySyncManager() {
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  }

  stopPeriodicSync();
  listeners.clear();
  console.log('[OfflineSync] Sync manager destroyed');
}

/**
 * Handle online event
 */
async function handleOnline() {
  console.log('[OfflineSync] Network restored');
  isOnline = true;
  emit('status_change', await getStatus());

  // Check for stale transactions
  await checkStaleTransactions();

  // Trigger sync
  triggerSync();
}

/**
 * Handle offline event
 */
async function handleOffline() {
  console.log('[OfflineSync] Network lost');
  isOnline = false;
  emit('status_change', await getStatus());
}

/**
 * Start periodic sync interval
 */
function startPeriodicSync() {
  if (syncIntervalId) return;

  syncIntervalId = setInterval(() => {
    if (isOnline && !isSyncing) {
      triggerSync();
    }
  }, SYNC_INTERVAL_MS);

  console.log(`[OfflineSync] Periodic sync started (every ${SYNC_INTERVAL_MS / 1000}s)`);
}

/**
 * Stop periodic sync interval
 */
function stopPeriodicSync() {
  if (syncIntervalId) {
    clearInterval(syncIntervalId);
    syncIntervalId = null;
    console.log('[OfflineSync] Periodic sync stopped');
  }
}

/**
 * Check for stale transactions (older than 24 hours)
 */
async function checkStaleTransactions() {
  const pending = await getPendingTransactions();
  const now = Date.now();

  for (const txn of pending) {
    if (now - txn.createdAt > STALE_THRESHOLD_MS) {
      console.log(`[OfflineSync] Marking stale transaction: ${txn.offlineId}`);
      await updateTransaction(txn.offlineId, {
        syncStatus: 'needs_review',
        errorMessage: 'Transaction is over 24 hours old - please review before syncing'
      });
      emit('stale_transaction', txn);
    }
  }
}

const SYNC_LOCK_KEY = 'offlineSync_lock';
const SYNC_LOCK_TTL_MS = 60000; // 60 วินาที — ถ้า tab crash lock จะหมดอายุเอง

function acquireSyncLock() {
  try {
    const existing = localStorage.getItem(SYNC_LOCK_KEY);
    if (existing) {
      const { timestamp } = JSON.parse(existing);
      if (Date.now() - timestamp < SYNC_LOCK_TTL_MS) return false; // tab อื่น sync อยู่
    }
    localStorage.setItem(SYNC_LOCK_KEY, JSON.stringify({ timestamp: Date.now() }));
    return true;
  } catch {
    return true; // localStorage ไม่ available — fallback ให้ sync ได้
  }
}

function releaseSyncLock() {
  try { localStorage.removeItem(SYNC_LOCK_KEY); } catch {}
}

/**
 * Trigger a sync operation
 */
export async function triggerSync() {
  if (isSyncing || !isOnline) {
    return;
  }

  if (!acquireSyncLock()) {
    console.log('[OfflineSync] Another tab is syncing — skipping');
    return;
  }

  isSyncing = true;
  emit('sync_start', { timestamp: Date.now() });
  emit('status_change', await getStatus());

  try {
    const pending = await getPendingTransactions();

    if (pending.length === 0) {
      console.log('[OfflineSync] No pending transactions');
      return;
    }

    console.log(`[OfflineSync] Syncing ${pending.length} transactions...`);

    // Sync one at a time to maintain order
    for (const txn of pending) {
      if (!isOnline) {
        console.log('[OfflineSync] Network lost during sync - stopping');
        break;
      }

      await syncTransaction(txn);
      emit('status_change', await getStatus());
    }

    emit('sync_complete', { timestamp: Date.now() });

  } catch (error) {
    console.error('[OfflineSync] Sync failed:', error);
    emit('sync_error', { error, timestamp: Date.now() });
  } finally {
    isSyncing = false;
    releaseSyncLock();
    emit('status_change', await getStatus());
  }
}

/**
 * Sync a single transaction
 */
async function syncTransaction(txn) {
  // Check if max retries exceeded
  if (txn.syncAttempts >= MAX_SYNC_ATTEMPTS) {
    console.log(`[OfflineSync] Max retries exceeded: ${txn.offlineId}`);
    await updateTransaction(txn.offlineId, {
      syncStatus: 'failed',
      errorMessage: `Max retries (${MAX_SYNC_ATTEMPTS}) exceeded`
    });
    emit('sync_failed', txn);
    return;
  }

  // Update status to 'syncing'
  await updateTransaction(txn.offlineId, {
    syncStatus: 'syncing',
    lastSyncAttempt: Date.now()
  });

  try {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      throw new Error('Admin token not found - cannot sync offline transactions');
    }

    // ใช้ branchId ที่บันทึกไว้ตอน queue ธุรกรรมนี้ (ตอนกดชำระเงินจริง) ไม่ใช่สาขาที่กำลัง
    // เลือกอยู่ตอนนี้ — ถ้าสลับสาขาระหว่างที่ออฟไลน์อยู่ installBranchFetch จะ fallback ไปใช้
    // ค่าปัจจุบันแทน ทำให้ธุรกรรมเก่าไปบันทึกผิดสาขาได้ ระบุ header เองตรงนี้กันไว้ก่อน
    const response = await fetch(`${apiUrl}/transactions/checkout-offline`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${adminToken}`,
        'X-Offline-Transaction-Id': txn.offlineId,
        'X-Table-Session-Id': txn.tableSnapshot?.sessionId || '',
        ...(txn.branchId ? { 'X-Branch-Id': txn.branchId } : {})
      },
      body: JSON.stringify({
        offlineId: txn.offlineId,
        tableCode: txn.tableCode,
        tableName: txn.tableName,
        guests: txn.guests,
        pricing: txn.pricing,
        orders: txn.orders,
        buffetTotal: txn.buffetTotal,
        menuTotal: txn.menuTotal,
        grandTotal: txn.grandTotal,
        paymentMethod: txn.paymentMethod,
        payments: txn.payments,
        notes: txn.notes,
        offlineCreatedAt: txn.createdAt,
        tableSessionId: txn.tableSnapshot?.sessionId
      })
    });

    const result = await response.json();

    if (response.ok) {
      // Success
      console.log(`[OfflineSync] Synced successfully: ${txn.offlineId}`);

      await updateTransaction(txn.offlineId, {
        syncStatus: 'synced',
        serverReceiptId: result.data?.transaction?.receiptId,
        serverTransactionId: result.data?.transaction?._id,
        syncedAt: Date.now()
      });

      await logSyncHistory(txn.offlineId, result.data?.transaction);
      emit('transaction_synced', { transaction: txn, serverResponse: result.data });

    } else if (response.status === 409) {
      // Conflict
      console.log(`[OfflineSync] Conflict detected: ${txn.offlineId}`);
      await handleConflict(txn, result);

    } else if (response.status === 400) {
      // Bad request - mark as failed
      console.log(`[OfflineSync] Bad request: ${txn.offlineId}`, result.message);
      await updateTransaction(txn.offlineId, {
        syncStatus: 'failed',
        errorMessage: result.message,
        syncAttempts: txn.syncAttempts + 1
      });

    } else {
      // Other error - will retry
      throw new Error(`HTTP ${response.status}: ${result.message || 'Unknown error'}`);
    }

  } catch (error) {
    // Network or other error - retry later
    console.error(`[OfflineSync] Error syncing ${txn.offlineId}:`, error);
    await updateTransaction(txn.offlineId, {
      syncStatus: 'pending',
      errorMessage: error.message,
      syncAttempts: txn.syncAttempts + 1
    });
  }
}

/**
 * Handle a conflict response from the server
 */
async function handleConflict(txn, serverResponse) {
  const conflictType = serverResponse.data?.conflictType || 'UNKNOWN';

  // Log the conflict
  await logConflict(txn.offlineId, conflictType, serverResponse, txn);

  // Auto-resolve certain conflicts
  if (conflictType === 'ALREADY_CHECKED_OUT' || conflictType === 'TABLE_ALREADY_CLOSED') {
    // These are safe to auto-resolve - the table was already charged
    console.log(`[OfflineSync] Auto-resolving conflict: ${txn.offlineId} (${conflictType})`);

    await updateTransaction(txn.offlineId, {
      syncStatus: 'resolved',
      errorMessage: serverResponse.message || 'Auto-resolved: Transaction already processed',
      resolution: 'auto_skip',
      resolvedAt: Date.now()
    });

    emit('conflict_auto_resolved', { transaction: txn, conflictType, serverResponse });

  } else {
    // Requires manual resolution
    await updateTransaction(txn.offlineId, {
      syncStatus: 'conflict',
      errorMessage: serverResponse.message
    });

    emit('conflict', { transaction: txn, conflictType, serverResponse });
  }
}

/**
 * Get current sync status
 */
export async function getStatus() {
  const counts = await getTransactionCounts();

  return {
    isOnline,
    isSyncing,
    pending: counts.pending,
    syncing: counts.syncing,
    synced: counts.synced,
    conflicts: counts.conflict,
    failed: counts.failed,
    needsReview: counts.needsReview,
    total: counts.total,
    lastSyncTime: Date.now()
  };
}

/**
 * Check if currently online
 */
export function getIsOnline() {
  return isOnline;
}

/**
 * Check if currently syncing
 */
export function getIsSyncing() {
  return isSyncing;
}

/**
 * Force a sync even if one is in progress
 */
export async function forceSync() {
  isSyncing = false;
  await triggerSync();
}

/**
 * Manually retry a failed transaction
 */
export async function retryTransaction(offlineId) {
  await updateTransaction(offlineId, {
    syncStatus: 'pending',
    syncAttempts: 0,
    errorMessage: null
  });

  triggerSync();
}

/**
 * Skip a transaction (mark as resolved without syncing)
 */
export async function skipTransaction(offlineId, reason) {
  await updateTransaction(offlineId, {
    syncStatus: 'resolved',
    resolution: 'manual_skip',
    errorMessage: reason || 'Manually skipped',
    resolvedAt: Date.now()
  });

  emit('status_change', await getStatus());
}

export default {
  initSyncManager,
  destroySyncManager,
  triggerSync,
  forceSync,
  getStatus,
  getIsOnline,
  getIsSyncing,
  subscribe,
  retryTransaction,
  skipTransaction
};
